import * as THREE from "three";
import type { WorldVisualLease } from "./WorldSystem";

export type ArchitectureKind =
  | "home"
  | "house"
  | "bridge"
  | "pavilion"
  | "canal-house"
  | "canal-house-balcony"
  | "canal-house-hipped"
  | "courtyard-enclosure"
  | "pocket-yard-edge"
  | "covered-gallery"
  | "stone-bridge"
  | "quay-dock"
  | "tea-room"
  | "life-props"
  | "life-breakfast"
  | "life-laundry"
  | "life-rain-gear"
  | "life-scooter"
  | "life-utility"
  | "life-repair"
  | "wupeng-boat";

export type ArchitectureCollisionRole = "solid" | "walkable" | "boundary" | "dynamic";

export interface ArchitectureCollisionMetadata {
  shape: "box";
  size: readonly [number, number, number];
  role: ArchitectureCollisionRole;
  surface?: "stone" | "wood";
}

export interface ArchitectureCollisionShape {
  id: string;
  shape: "obb";
  centerX: number;
  centerZ: number;
  halfWidth: number;
  halfDepth: number;
  rotation: number;
  enabled?: boolean;
}

export interface ArchitectureWalkableSurface {
  id: string;
  shape: "obb";
  centerX: number;
  centerZ: number;
  halfWidth: number;
  halfDepth: number;
  rotation: number;
  height: number;
  surface: "stone" | "wood";
}

export interface ArchitectureTemplateMetadata {
  id: ArchitectureKind;
  category: "building" | "passage" | "bridge" | "waterside" | "interior" | "life" | "boat";
  dimensions: readonly [number, number, number];
  enterable: boolean;
  waterfrontSide?: "+z" | "-z";
}

export interface ArchitectureMaterials {
  pavers: THREE.MeshStandardMaterial;
  stone: THREE.MeshStandardMaterial;
  wood: THREE.MeshStandardMaterial;
  water?: THREE.Material;
}

const shadow = (object: THREE.Object3D): void => {
  object.traverse((child) => {
    if (!(child instanceof THREE.Mesh)) return;
    child.castShadow = true;
    child.receiveShadow = true;
  });
};

export class ArchitectureLibrary {
  private readonly templates = new Map<ArchitectureKind, THREE.Group>();
  private readonly active = new Set<THREE.Object3D>();
  private readonly boxGeometries = new Map<string, THREE.BoxGeometry>();
  private readonly ownedGeometries = new Set<THREE.BufferGeometry>();
  private readonly ownedMaterials = new Set<THREE.Material>();
  private readonly plaster: THREE.MeshStandardMaterial;
  private readonly roof: THREE.MeshStandardMaterial;
  private readonly glass: THREE.MeshPhysicalMaterial;
  private readonly mutedCloth: THREE.MeshStandardMaterial;
  private readonly cardboard: THREE.MeshStandardMaterial;
  private readonly paintedMetal: THREE.MeshStandardMaterial;
  private readonly paleMetal: THREE.MeshStandardMaterial;
  private readonly canalWater: THREE.Material;
  private readonly foliage: THREE.MeshStandardMaterial;
  private readonly dampPlaster: THREE.MeshStandardMaterial;
  private disposed = false;

  constructor(private readonly materials: ArchitectureMaterials) {
    this.plaster = this.ownMaterial(new THREE.MeshStandardMaterial({
      name: "lime-plaster",
      color: 0xd9d5c8,
      roughness: 0.96,
      metalness: 0,
      roughnessMap: materials.stone.roughnessMap,
    }));
    this.roof = this.ownMaterial(new THREE.MeshStandardMaterial({
      name: "weathered-roof-tile",
      color: 0x53615d,
      roughness: 0.88,
      metalness: 0,
      side: THREE.DoubleSide,
      roughnessMap: materials.stone.roughnessMap,
    }));
    this.glass = this.ownMaterial(new THREE.MeshPhysicalMaterial({
      name: "smoky-window-glass",
      color: 0x789292,
      roughness: 0.18,
      metalness: 0,
      transmission: 0.18,
      transparent: true,
      opacity: 0.82,
    }));
    this.mutedCloth = this.ownMaterial(new THREE.MeshStandardMaterial({
      name: "muted-indigo-cloth",
      color: 0x65736f,
      roughness: 0.92,
      side: THREE.DoubleSide,
    }));
    this.cardboard = this.ownMaterial(new THREE.MeshStandardMaterial({
      name: "unprinted-cardboard",
      color: 0x927c5d,
      roughness: 0.94,
    }));
    this.paintedMetal = this.ownMaterial(new THREE.MeshStandardMaterial({
      name: "dark-painted-metal",
      color: 0x263332,
      roughness: 0.62,
      metalness: 0.35,
    }));
    this.paleMetal = this.ownMaterial(new THREE.MeshStandardMaterial({
      name: "aged-pale-metal",
      color: 0xb8b8ac,
      roughness: 0.74,
      metalness: 0.22,
    }));
    this.canalWater = materials.water ?? this.ownMaterial(new THREE.MeshStandardMaterial({
      name: "rain-muted-canal-water-fallback",
      color: 0x415b55,
      roughness: 0.88,
      metalness: 0,
    }));
    this.foliage = this.ownMaterial(new THREE.MeshStandardMaterial({
      name: "courtyard-foliage",
      color: 0x526b50,
      roughness: 0.96,
      metalness: 0,
    }));
    this.dampPlaster = this.ownMaterial(new THREE.MeshStandardMaterial({
      name: "damp-plaster-skirt",
      color: 0x70766a,
      roughness: 0.98,
      metalness: 0,
      roughnessMap: materials.stone.roughnessMap,
    }));
  }

  acquire(kind: ArchitectureKind): WorldVisualLease {
    if (this.disposed) throw new Error("ArchitectureLibrary has been disposed");
    const template = this.templates.get(kind) ?? this.createTemplate(kind);
    this.templates.set(kind, template);
    const object = template.clone(true);
    this.active.add(object);
    let released = false;
    return {
      object,
      release: () => {
        if (released) return;
        released = true;
        object.removeFromParent();
        this.active.delete(object);
      },
    };
  }

  dispose(): void {
    if (this.disposed) return;
    this.disposed = true;
    for (const object of this.active) object.removeFromParent();
    this.active.clear();
    this.templates.clear();
    for (const geometry of this.ownedGeometries) geometry.dispose();
    this.ownedGeometries.clear();
    this.boxGeometries.clear();
    for (const material of this.ownedMaterials) material.dispose();
    this.ownedMaterials.clear();
  }

  private createTemplate(kind: ArchitectureKind): THREE.Group {
    let template: THREE.Group;
    switch (kind) {
      case "home":
        template = this.createHome();
        break;
      case "house":
      case "canal-house":
      case "canal-house-balcony":
      case "canal-house-hipped":
        template = this.createCanalHouse(kind);
        break;
      case "courtyard-enclosure":
        template = this.createCourtyardEnclosure();
        break;
      case "pocket-yard-edge":
        template = this.createPocketYardEdge();
        break;
      case "bridge":
      case "stone-bridge":
        template = this.createStoneBridge(kind);
        break;
      case "pavilion":
        template = this.createPavilion();
        break;
      case "covered-gallery":
        template = this.createCoveredGallery();
        break;
      case "quay-dock":
        template = this.createQuayDock();
        break;
      case "tea-room":
        template = this.createTeaRoom();
        break;
      case "life-props":
      case "life-breakfast":
      case "life-laundry":
      case "life-rain-gear":
      case "life-scooter":
      case "life-utility":
      case "life-repair":
        template = this.createLifeProps(kind);
        break;
      case "wupeng-boat":
        template = this.createWupengBoat();
        break;
    }
    template.name = `architecture-${kind}`;
    this.compileSpatialMetadata(template);
    shadow(template);
    return template;
  }

  private createHome(): THREE.Group {
    const home = this.template("home", "building", [22, 6.4, 23], false);
    home.add(this.box("home-landing", [10.2, 0.18, 9.2], this.materials.pavers, [0, 0.09, 8.8], undefined, "walkable", "stone"));
    home.add(this.box("home-canal-water", [8.1, 0.1, 15.7], this.canalWater, [0, 0.055, -1.75]));
    for (const x of [-4.3, 4.3]) {
      home.add(this.box("home-revetment", [0.48, 1.02, 15.8], this.materials.stone, [x, 0.02, -1.75], undefined, "boundary"));
      home.add(this.box("home-bank-walk", [2.25, 0.18, 15.8], this.materials.pavers, [x < 0 ? -5.65 : 5.65, 0.09, -1.75], undefined, "walkable", "stone"));
    }

    const leftHouse = this.createCanalHouse("house");
    leftHouse.name = "HomeLeftCanalHouse";
    leftHouse.position.set(-8.05, 0.18, -1.15);
    leftHouse.rotation.y = Math.PI * 0.5;
    leftHouse.scale.set(0.83, 0.94, 0.83);
    const rightHouse = this.createCanalHouse("canal-house");
    rightHouse.name = "HomeRightCanalHouse";
    rightHouse.position.set(8.05, 0.18, -3.55);
    rightHouse.rotation.y = -Math.PI * 0.5;
    rightHouse.scale.set(0.76, 0.8, 0.76);
    home.add(leftHouse, rightHouse);

    const bridge = this.createStoneBridge("stone-bridge");
    bridge.name = "HomeCanalBridge";
    bridge.position.set(0, 0.12, -5.2);
    bridge.rotation.y = Math.PI * 0.5;
    bridge.scale.set(0.74, 0.9, 0.74);
    home.add(bridge);

    const mooredBoat = this.createWupengBoat();
    mooredBoat.name = "HomeMooredWupengBoat";
    mooredBoat.position.set(2.45, -0.22, -1.7);
    mooredBoat.rotation.y = Math.PI;
    mooredBoat.scale.setScalar(0.72);
    delete mooredBoat.userData.motion;
    home.add(mooredBoat);

    const leftDistanceHouse = this.createCanalHouse("house");
    leftDistanceHouse.name = "HomeLeftDistanceHouse";
    leftDistanceHouse.position.set(-9.6, 0.16, -14.8);
    leftDistanceHouse.rotation.y = Math.PI * 0.5;
    leftDistanceHouse.scale.set(0.72, 0.78, 0.72);
    const rightDistanceHouse = this.createCanalHouse("canal-house");
    rightDistanceHouse.name = "HomeRightDistanceHouse";
    rightDistanceHouse.position.set(9.2, 0.16, -14.1);
    rightDistanceHouse.rotation.y = -Math.PI * 0.5;
    rightDistanceHouse.scale.set(0.68, 0.88, 0.68);
    home.add(leftDistanceHouse, rightDistanceHouse);

    for (let index = 0; index < 3; index += 1) {
      const y = 0.18 - index * 0.12;
      const z = 4.48 - index * 0.48;
      home.add(this.box("home-water-step", [3.2, 0.18, 0.52], this.materials.stone, [0, y, z], undefined, "walkable", "stone"));
    }
    const mooring = new THREE.Group();
    mooring.name = "HomeMooring";
    mooring.userData.semantic = "boat-mooring";
    mooring.position.set(3.58, 0, 2.1);
    mooring.add(this.box("home-mooring-post", [0.24, 0.82, 0.24], this.materials.wood, [0, 0.41, 0], undefined, "solid"));
    mooring.add(this.anchor("HomeBoatMooringAnchor", "boat-mooring", [-0.4, 0.18, 0], -Math.PI * 0.5));
    home.add(mooring);

    const gate = new THREE.Group();
    gate.name = "GatePivot";
    gate.position.set(-6.9, 0, -8.4);
    gate.rotation.y = Math.PI * 0.5;
    gate.userData.interaction = {
      id: "courtyard-gate",
      label: "推开院门",
      kind: "gate",
      open: false,
      closedRotation: Math.PI * 0.5,
    };
    gate.userData.semantic = "hinged-gate";
    gate.userData.collisionShapeIds = ["courtyard-gate-left", "courtyard-gate-right"];
    gate.add(this.box("gate-left", [2, 2.05, 0.16], this.materials.wood, [1, 1.03, 0], undefined, "solid", undefined, "courtyard-gate-left"));
    gate.add(this.box("gate-right", [2, 2.05, 0.16], this.materials.wood, [3, 1.03, 0], undefined, "solid", undefined, "courtyard-gate-right"));
    for (const z of [-10.85, -5.95]) {
      home.add(this.box("gate-post", [0.64, 2.75, 0.58], this.materials.wood, [-6.9, 1.38, z], undefined, "solid"));
      home.add(this.box("gate-plinth", [0.84, 0.35, 0.78], this.materials.stone, [-6.9, 0.18, z], undefined, "solid"));
    }
    home.add(this.box("gate-lintel", [0.78, 0.48, 5.8], this.materials.wood, [-6.9, 2.63, -8.4]));
    const gateRoof = this.createGabledRoof(6.5, 1.5, 0.55);
    gateRoof.position.set(-6.9, 2.88, -8.4);
    gateRoof.rotation.y = Math.PI * 0.5;
    home.add(gate, gateRoof);

    const bench = new THREE.Group();
    bench.name = "CourtyardBench";
    bench.position.set(6.8, 0.4, 7.2);
    bench.userData.interaction = { id: "courtyard-bench", label: "坐一会儿", kind: "bench" };
    bench.userData.semantic = "seat";
    bench.add(this.box("bench-seat", [3.2, 0.18, 0.72], this.materials.wood, [0, 0.58, 0], undefined, "solid"));
    bench.add(this.box("bench-back", [3.2, 0.16, 0.68], this.materials.wood, [0, 0.95, 0.3], [-0.18, 0, 0], "solid"));
    for (const x of [-1.2, 1.2]) bench.add(this.box("bench-leg", [0.2, 0.62, 0.56], this.materials.wood, [x, 0.3, 0], undefined, "solid"));
    bench.add(this.anchor("BenchSitAnchor", "seat", [0, 0.64, -0.72], Math.PI));
    home.add(bench);

    const laundry = this.createLaundryLine();
    laundry.name = "HomeLaundryLine";
    laundry.position.set(-7.2, 0.18, 4.35);
    laundry.rotation.y = Math.PI * 0.5;
    home.add(laundry);
    const plants = this.createPlantTubs();
    plants.name = "HomeWatersidePlants";
    plants.position.set(5.3, 0.18, 4.55);
    home.add(plants);
    home.add(this.anchor("HomeCanalViewAnchor", "waterfront-view", [0, 0.28, 4.9], Math.PI));
    return home;
  }

  private createCanalHouse(id: "house" | "canal-house" | "canal-house-balcony" | "canal-house-hipped"): THREE.Group {
    const house = this.template(id, "building", [5.8, 7.1, 9.4], false, "+z");
    const width = 5.4;
    const depth = 8.8;
    house.add(this.box("stone-foundation", [5.8, 0.48, 9.2], this.materials.stone, [0, 0.24, 0], undefined, "solid"));
    house.add(this.box("plaster-wall", [width, 3.65, depth], this.plaster, [0, 2.05, 0], undefined, "solid"));
    house.add(this.box("upper-plaster-wall", [width - 0.12, 2.35, depth - 0.12], this.plaster, [0, 5.02, 0], undefined, "solid"));
    for (const x of [-width * 0.5 - 0.012, width * 0.5 + 0.012]) {
      house.add(this.box("damp-wall-skirt", [0.035, 0.72, depth - 0.18], this.dampPlaster, [x, 0.78, 0]));
      for (const z of [-2.45, 0.15, 2.75]) {
        const sideWindow = this.createLatticeWindow(0, 2.55, z, 0.96, 1.18);
        sideWindow.name = "side-lattice-window";
        sideWindow.position.x = x;
        sideWindow.rotation.y = x < 0 ? -Math.PI * 0.5 : Math.PI * 0.5;
        house.add(sideWindow);
      }
    }
    for (const x of [-2.58, -1.75, 0, 1.75, 2.58]) {
      house.add(this.box("timber-post", [0.18, 5.9, 0.2], this.materials.wood, [x, 3.52, 4.43]));
    }
    for (const y of [0.54, 3.86, 6.36]) {
      house.add(this.box("timber-beam", [5.35, 0.18, 0.22], this.materials.wood, [0, y, 4.43]));
    }
    const doorX = id === "canal-house-hipped" ? -1.25 : 0;
    house.add(this.box("front-door", [1.15, 2.48, 0.14], this.materials.wood, [doorX, 1.76, 4.47]));
    const lowerWindows = id === "canal-house-hipped" ? [0.5, 1.8] : [-1.75, 1.75];
    for (const x of lowerWindows) house.add(this.createLatticeWindow(x, 2.15, 4.48, 1.15, 1.25));
    for (const x of [-1.75, 0, 1.75]) house.add(this.createLatticeWindow(x, 5.15, 4.48, 1.05, 1.15));
    const roof = id === "canal-house-hipped"
      ? this.createHipRoof(6.55, 10.15, 1.42)
      : this.createGabledRoof(6.35, 10.1, id === "canal-house-balcony" ? 1.08 : 1.32);
    roof.position.y = 6.25;
    house.add(roof);
    if (id === "canal-house-balcony") {
      const balcony = new THREE.Group();
      balcony.name = "CanalHouseBalcony";
      balcony.userData.semantic = "domestic-balcony";
      balcony.add(this.box("balcony-floor", [3.75, 0.18, 0.92], this.materials.wood, [0, 3.86, 4.84], undefined, "solid"));
      balcony.add(this.box("balcony-rail", [3.75, 0.12, 0.12], this.materials.wood, [0, 4.55, 5.23], undefined, "boundary"));
      for (const x of [-1.72, -0.86, 0, 0.86, 1.72]) {
        balcony.add(this.box("balcony-rail-post", [0.1, 0.72, 0.1], this.materials.wood, [x, 4.2, 5.23], undefined, "boundary"));
      }
      house.add(balcony);
    } else if (id === "canal-house-hipped") {
      const hood = this.box("offset-entry-hood", [2.15, 0.14, 0.9], this.roof, [-1.25, 3.18, 4.78], [-0.12, 0, 0]);
      house.add(hood);
    }
    house.add(this.box("canal-doorstep", [1.8, 0.2, 0.7], this.materials.stone, [0, 0.14, 4.82], undefined, "walkable", "stone"));
    house.add(this.anchor("CanalFrontAnchor", "waterfront-entry", [0, 0.25, 5.25], 0));
    house.add(this.anchor("StreetRearAnchor", "street-entry", [0, 0.25, -4.9], Math.PI));
    return house;
  }

  private createCoveredGallery(): THREE.Group {
    const gallery = this.template("covered-gallery", "passage", [12.8, 4.5, 3.2], false, "+z");
    gallery.add(this.box("gallery-walkway", [12.4, 0.28, 2.75], this.materials.pavers, [0, 0.14, 0], undefined, "walkable", "stone"));
    gallery.add(this.box("gallery-landward-sill", [12.4, 0.42, 0.28], this.materials.stone, [0, 0.35, -1.23], undefined, "boundary"));
    for (const x of [-5.7, -3.8, -1.9, 0, 1.9, 3.8, 5.7]) {
      gallery.add(this.box("gallery-post", [0.2, 3.25, 0.2], this.materials.wood, [x, 1.82, -1.08], undefined, "solid"));
      gallery.add(this.box("gallery-post", [0.2, 3.25, 0.2], this.materials.wood, [x, 1.82, 1.08], undefined, "solid"));
    }
    for (const z of [-1.08, 1.08]) {
      gallery.add(this.box("gallery-eave-beam", [12.4, 0.22, 0.22], this.materials.wood, [0, 3.36, z]));
    }
    for (const x of [-4.75, -2.85, -0.95, 0.95, 2.85, 4.75]) {
      gallery.add(this.box("gallery-rail-post", [0.14, 0.82, 0.14], this.materials.wood, [x, 0.62, 1.16], undefined, "boundary"));
    }
    gallery.add(this.box("gallery-canal-rail", [11.8, 0.14, 0.16], this.materials.wood, [0, 0.94, 1.16], undefined, "boundary"));
    const roof = this.createGabledRoof(13.1, 3.55, 0.78);
    roof.position.y = 3.43;
    gallery.add(roof);
    gallery.add(this.anchor("GalleryPathStart", "path-start", [-6.1, 0.3, 0], -Math.PI * 0.5));
    gallery.add(this.anchor("GalleryPathEnd", "path-end", [6.1, 0.3, 0], Math.PI * 0.5));
    gallery.add(this.anchor("GalleryCanalEdge", "waterfront-view", [0, 0.3, 0.82], 0));
    return gallery;
  }

  private createCourtyardEnclosure(): THREE.Group {
    const enclosure = this.template("courtyard-enclosure", "building", [8.4, 4.7, 6.2], false, "+z");
    enclosure.userData.semantic = "courtyard-boundary";
    enclosure.add(this.box("enclosure-foundation", [7.8, 0.34, 2.5], this.materials.stone, [0, 0.17, -1.72], undefined, "solid"));
    enclosure.add(this.box("enclosure-side-wing", [7.5, 2.9, 2.25], this.plaster, [0, 1.62, -1.72], undefined, "solid"));
    const roof = this.createGabledRoof(8.1, 3.05, 0.92);
    roof.position.set(0, 3.08, -1.72);
    enclosure.add(roof);

    for (const x of [-3.68, 3.68]) {
      enclosure.add(this.box("courtyard-side-wall", [0.24, 1.82, 4.05], this.plaster, [x, 0.91, 0.98], undefined, "boundary"));
      enclosure.add(this.box("courtyard-wall-cap", [0.42, 0.16, 4.25], this.roof, [x, 1.86, 0.98]));
    }
    for (const x of [-2.62, 2.62]) {
      enclosure.add(this.box("courtyard-gate-wall", [2.15, 1.82, 0.24], this.plaster, [x, 0.91, 3], undefined, "boundary"));
      enclosure.add(this.box("courtyard-gate-cap", [2.35, 0.16, 0.42], this.roof, [x, 1.86, 3]));
    }
    enclosure.add(this.box("courtyard-gate-post", [0.3, 2.35, 0.3], this.materials.wood, [-1.42, 1.18, 3], undefined, "boundary"));
    enclosure.add(this.box("courtyard-gate-post", [0.3, 2.35, 0.3], this.materials.wood, [1.42, 1.18, 3], undefined, "boundary"));
    enclosure.add(this.box("courtyard-gate-lintel", [3.15, 0.28, 0.34], this.materials.wood, [0, 2.24, 3]));
    enclosure.add(this.anchor("CourtyardGateAnchor", "courtyard-entry", [0, 0.2, 3.38], 0));
    return enclosure;
  }

  private createPocketYardEdge(): THREE.Group {
    const edge = this.template("pocket-yard-edge", "passage", [7.2, 3.5, 2.4], false);
    edge.userData.semantic = "neighborhood-yard-edge";

    edge.add(this.box("pocket-side-shed", [2.2, 2.35, 1.8], this.plaster, [-2.4, 1.18, -0.18], undefined, "solid"));
    const roof = this.createGabledRoof(2.55, 2.25, 0.62);
    roof.name = "PocketSideShedRoof";
    roof.position.set(-2.4, 2.36, -0.18);
    edge.add(roof);

    edge.add(this.box("pocket-low-wall", [2.8, 1.02, 0.26], this.plaster, [1.7, 0.51, 0.18], undefined, "boundary"));
    edge.add(this.box("pocket-wall-cap", [3, 0.14, 0.42], this.roof, [1.7, 1.07, 0.18]));

    const plants = this.createPlantTubs();
    plants.name = "PocketBoundaryPlants";
    plants.position.set(1.45, 0, 0.62);
    plants.scale.setScalar(0.78);
    edge.add(plants);

    const tree = new THREE.Group();
    tree.name = "PocketBoundaryTree";
    tree.userData.semantic = "small-boundary-tree";
    const trunk = new THREE.Mesh(this.ownGeometry(new THREE.CylinderGeometry(0.11, 0.16, 1.75, 8)), this.materials.wood);
    trunk.name = "boundary-tree-trunk";
    trunk.position.y = 0.88;
    tree.add(trunk);
    for (const [x, y, z, scale] of [
      [0, 1.92, 0, 0.72],
      [-0.3, 1.72, 0.05, 0.52],
      [0.28, 1.68, -0.08, 0.48],
    ] as const) {
      const crown = new THREE.Mesh(this.ownGeometry(new THREE.SphereGeometry(scale, 7, 5)), this.foliage);
      crown.name = "boundary-tree-crown";
      crown.position.set(x, y, z);
      crown.scale.y = 0.78;
      tree.add(crown);
    }
    tree.position.set(3.02, 0, -0.2);
    edge.add(tree);
    edge.add(this.anchor("PocketYardOpeningAnchor", "square-opening", [-0.52, 0.16, 0.9], 0));
    return edge;
  }

  private createStoneBridge(id: "bridge" | "stone-bridge"): THREE.Group {
    const bridge = this.template(id, "bridge", [4.9, 2.25, 13.2], false);
    const segments = 31;
    const length = 12.6;
    const segmentLength = length / segments;
    const deckVisual = new THREE.Mesh(
      this.ownGeometry(this.createArchedStripGeometry(4.45, length, 1.28, 0.34, segments)),
      this.materials.pavers,
    );
    deckVisual.name = "bridge-smooth-deck";
    bridge.add(deckVisual);
    const parapetGeometry = this.ownGeometry(this.createArchedStripGeometry(0.28, length, 1.28, 0.66, segments));
    for (const x of [-2.08, 2.08]) {
      const parapet = new THREE.Mesh(parapetGeometry, this.materials.stone);
      parapet.name = "bridge-smooth-parapet";
      parapet.position.set(x, 0.48, 0);
      bridge.add(parapet);
    }
    const archFaceGeometry = this.ownGeometry(this.createBridgeArchFaceGeometry(length, 1.28, 0.78, segments));
    for (const x of [-2.24, 2.24]) {
      const archFace = new THREE.Mesh(archFaceGeometry, this.materials.stone);
      archFace.name = "bridge-stone-arch-face";
      archFace.position.x = x;
      archFace.rotation.y = x < 0 ? -Math.PI * 0.5 : Math.PI * 0.5;
      bridge.add(archFace);
    }
    for (let index = 0; index < segments; index += 1) {
      const t = index / (segments - 1);
      const z = (t - 0.5) * length;
      const arch = Math.sin(t * Math.PI) * 1.28;
      const nextT = Math.min(1, (index + 1) / (segments - 1));
      const nextArch = Math.sin(nextT * Math.PI) * 1.28;
      const tilt = Math.atan2(nextArch - arch, length / (segments - 1));
      const deckCollision = this.box("bridge-deck-block", [4.45, 0.36, segmentLength + 0.08], this.materials.pavers, [0, arch, z], [tilt, 0, 0], "walkable", "stone");
      deckCollision.visible = false;
      bridge.add(deckCollision);
      for (const x of [-2.08, 2.08]) {
        const parapetCollision = this.box("bridge-parapet", [0.28, 0.66, segmentLength + 0.06], this.materials.stone, [x, arch + 0.48, z], [tilt, 0, 0], "boundary");
        parapetCollision.visible = false;
        bridge.add(parapetCollision);
      }
    }
    for (const z of [-6.55, 6.55]) {
      bridge.add(this.box("bridge-landing", [4.9, 0.24, 0.75], this.materials.stone, [0, -0.08, z], undefined, "walkable", "stone"));
    }
    bridge.add(this.anchor("BridgeApproachNorth", "bridge-approach", [0, 0.1, -6.9], Math.PI));
    bridge.add(this.anchor("BridgeApproachSouth", "bridge-approach", [0, 0.1, 6.9], 0));
    bridge.add(this.anchor("BridgeCrownAnchor", "viewpoint", [0, 1.48, 0], 0));
    return bridge;
  }

  private createArchedStripGeometry(
    width: number,
    length: number,
    rise: number,
    thickness: number,
    segments: number,
  ): THREE.BufferGeometry {
    const positions: number[] = [];
    const indices: number[] = [];
    for (let index = 0; index <= segments; index += 1) {
      const t = index / segments;
      const z = (t - 0.5) * length;
      const top = Math.sin(t * Math.PI) * rise;
      for (const x of [-width * 0.5, width * 0.5]) {
        positions.push(x, top, z, x, top - thickness, z);
      }
    }
    for (let index = 0; index < segments; index += 1) {
      const a = index * 4;
      const b = a + 4;
      indices.push(
        a, b, a + 2, a + 2, b, b + 2,
        a + 1, a + 3, b + 1, a + 3, b + 3, b + 1,
        a, a + 1, b, a + 1, b + 1, b,
        a + 2, b + 2, a + 3, a + 3, b + 2, b + 3,
      );
    }
    indices.push(0, 2, 1, 1, 2, 3);
    const end = segments * 4;
    indices.push(end, end + 1, end + 2, end + 1, end + 3, end + 2);
    const geometry = new THREE.BufferGeometry();
    geometry.setAttribute("position", new THREE.Float32BufferAttribute(positions, 3));
    geometry.setIndex(indices);
    geometry.computeVertexNormals();
    geometry.computeBoundingSphere();
    return geometry;
  }

  private createBridgeArchFaceGeometry(
    length: number,
    topRise: number,
    openingRise: number,
    segments: number,
  ): THREE.BufferGeometry {
    const positions: number[] = [];
    const indices: number[] = [];
    for (let index = 0; index <= segments; index += 1) {
      const t = index / segments;
      const z = (t - 0.5) * length;
      const top = Math.sin(t * Math.PI) * topRise - 0.08;
      const opening = Math.sin(t * Math.PI) * openingRise - 0.28;
      positions.push(z, top, 0, z, opening, 0);
    }
    for (let index = 0; index < segments; index += 1) {
      const offset = index * 2;
      indices.push(offset, offset + 2, offset + 1, offset + 1, offset + 2, offset + 3);
    }
    const geometry = new THREE.BufferGeometry();
    geometry.setAttribute("position", new THREE.Float32BufferAttribute(positions, 3));
    geometry.setIndex(indices);
    geometry.computeVertexNormals();
    geometry.computeBoundingSphere();
    return geometry;
  }

  private createQuayDock(): THREE.Group {
    const quay = this.template("quay-dock", "waterside", [7.2, 1.8, 6.8], false, "+z");
    quay.add(this.box("quay-platform", [7.2, 0.42, 3.2], this.materials.stone, [0, 0.21, -1.55], undefined, "walkable", "stone"));
    const stepDepth = 0.72;
    for (let index = 0; index < 4; index += 1) {
      const y = 0.28 - index * 0.2;
      const z = 0.42 + index * stepDepth;
      quay.add(this.box("quay-step", [4.1, 0.24, stepDepth + 0.05], this.materials.stone, [0, y, z], undefined, "walkable", "stone"));
    }
    quay.add(this.box("timber-dock-deck", [3.4, 0.18, 2.05], this.materials.wood, [-1.75, -0.52, 4.35], undefined, "walkable", "wood"));
    for (const x of [-3.25, -0.25]) {
      quay.add(this.box("dock-pile", [0.22, 1.7, 0.22], this.materials.wood, [x, -0.48, 4.85], undefined, "solid"));
    }
    for (const x of [-3.15, 3.15]) {
      quay.add(this.box("quay-edge-marker", [0.24, 0.68, 0.24], this.materials.stone, [x, 0.55, 0.02], undefined, "boundary"));
    }
    quay.add(this.anchor("QuayStreetAnchor", "street-entry", [0, 0.45, -3.05], Math.PI));
    quay.add(this.anchor("QuayWaterAnchor", "water-access", [0, -0.25, 3.15], 0));
    quay.add(this.anchor("BoatMooringAnchor", "boat-mooring", [-1.75, -0.38, 5.55], 0));
    return quay;
  }

  private createTeaRoom(): THREE.Group {
    const room = this.template("tea-room", "interior", [7.2, 5.35, 8.2], true, "+z");
    room.add(this.box("tea-room-floor", [7.1, 0.22, 8.1], this.materials.pavers, [0, 0.11, 0], undefined, "walkable", "stone"));
    room.add(this.box("tea-room-rear-wall", [7.1, 3.9, 0.28], this.plaster, [0, 2.05, -3.92], undefined, "solid"));
    for (const x of [-3.42, 3.42]) {
      room.add(this.box("tea-room-side-wall", [0.28, 3.9, 8.1], this.plaster, [x, 2.05, 0], undefined, "solid"));
    }
    for (const x of [-2.72, 2.72]) {
      room.add(this.box("tea-room-front-pier", [1.36, 3.9, 0.28], this.plaster, [x, 2.05, 3.92], undefined, "solid"));
    }
    room.add(this.box("tea-room-front-lintel", [4.1, 0.46, 0.32], this.materials.wood, [0, 3.68, 3.92], undefined, "solid"));
    for (const x of [-1.55, 1.55]) {
      room.add(this.box("tea-room-screen", [1.05, 2.75, 0.12], this.materials.wood, [x, 1.7, 3.93], undefined, "solid"));
    }
    const roof = this.createGabledRoof(7.85, 9.05, 1.28);
    roof.position.y = 3.88;
    room.add(roof);

    const counter = new THREE.Group();
    counter.name = "TeaCounter";
    counter.userData.semantic = "service-counter";
    counter.add(this.box("tea-counter-body", [3.4, 0.95, 0.78], this.materials.wood, [0, 0.58, 0], undefined, "solid"));
    counter.position.set(1.35, 0.12, -2.85);
    room.add(counter);
    for (const z of [-1.15, 1.15]) {
      room.add(this.box("tea-table-top", [1.25, 0.12, 1.25], this.materials.wood, [-1.35, 0.82, z], undefined, "solid"));
      room.add(this.box("tea-table-leg", [0.18, 0.72, 0.18], this.materials.wood, [-1.35, 0.42, z], undefined, "solid"));
      for (const x of [-2.35, -0.35]) {
        room.add(this.box("tea-stool", [0.52, 0.45, 0.52], this.materials.wood, [x, 0.34, z], undefined, "solid"));
      }
      room.add(this.anchor(z < 0 ? "TeaSeatNorth" : "TeaSeatSouth", "seat", [-2.35, 0.5, z], Math.PI * 0.5));
    }
    room.add(this.anchor("TeaRoomEntryAnchor", "interior-entry", [0, 0.25, 4.25], 0));
    room.add(this.anchor("TeaServiceAnchor", "service", [1.35, 0.25, -1.95], Math.PI));
    return room;
  }

  private createLifeProps(
    kind: "life-props" | "life-breakfast" | "life-laundry" | "life-rain-gear" | "life-scooter" | "life-utility" | "life-repair",
  ): THREE.Group {
    const dimensions: Record<typeof kind, readonly [number, number, number]> = {
      "life-props": [4.8, 2.6, 2.2],
      "life-breakfast": [4.4, 2.3, 2.8],
      "life-laundry": [4.8, 2.4, 1.8],
      "life-rain-gear": [3.2, 2.2, 1.6],
      "life-scooter": [3.2, 1.5, 1.5],
      "life-utility": [3.8, 2.5, 1.6],
      "life-repair": [4.5, 1.8, 2.2],
    };
    const life = this.template(kind, "life", dimensions[kind], false);
    life.userData.semantic = "contemporary-daily-life";

    if (kind === "life-props" || kind === "life-laundry") {
      life.add(this.createLaundryLine());
      if (kind === "life-laundry") {
        life.add(this.box("laundry-basin", [0.82, 0.34, 0.62], this.paleMetal, [1.35, 0.17, 0.15], undefined, "solid"));
        life.add(this.box("folded-basket", [0.68, 0.26, 0.5], this.materials.wood, [2.02, 0.13, -0.12], undefined, "solid"));
      }
    }
    if (kind === "life-breakfast") life.add(this.createBreakfastSetting());
    if (kind === "life-rain-gear") life.add(this.createRainGearRack());
    if (kind === "life-props" || kind === "life-scooter") life.add(this.createScooter());
    if (kind === "life-props" || kind === "life-utility") life.add(this.createUtilityCluster());
    if (kind === "life-props" || kind === "life-repair") life.add(this.createRepairSetting());
    return life;
  }

  private createBreakfastSetting(): THREE.Group {
    const breakfast = new THREE.Group();
    breakfast.name = "BreakfastSetting";
    breakfast.userData.semantic = "neighborhood-breakfast";
    breakfast.add(this.box("breakfast-counter", [2.35, 0.86, 0.72], this.materials.wood, [0, 0.43, 0], undefined, "solid"));
    breakfast.add(this.box("breakfast-worktop", [2.55, 0.12, 0.9], this.paleMetal, [0, 0.91, 0], undefined, "solid"));
    for (const x of [-0.52, 0.2, 0.88]) {
      const steamer = new THREE.Mesh(this.ownGeometry(new THREE.CylinderGeometry(0.27, 0.29, 0.16, 12)), this.materials.wood);
      steamer.name = "bamboo-steamer";
      steamer.position.set(x, 1.07, 0);
      breakfast.add(steamer);
    }
    for (const x of [-1.55, 1.55]) breakfast.add(this.box("breakfast-stool", [0.46, 0.42, 0.46], this.materials.wood, [x, 0.21, 0.82], undefined, "solid"));
    breakfast.add(this.box("plain-rain-awning", [4.1, 0.12, 2.25], this.mutedCloth, [0, 2.08, 0.12], [-0.08, 0, 0]));
    return breakfast;
  }

  private createRainGearRack(): THREE.Group {
    const rack = new THREE.Group();
    rack.name = "RainGearRack";
    rack.userData.semantic = "daily-rain-gear";
    rack.add(this.box("rain-rack-post", [0.12, 1.95, 0.12], this.paintedMetal, [-1.1, 0.98, 0], undefined, "solid"));
    rack.add(this.box("rain-rack-bar", [2.2, 0.1, 0.1], this.paintedMetal, [0, 1.78, 0]));
    for (const x of [-0.55, 0.15, 0.82]) {
      const cape = this.box("hanging-rain-cape", [0.54, 1.08, 0.04], this.mutedCloth, [x, 1.16, 0]);
      cape.rotation.z = x * 0.05;
      rack.add(cape);
    }
    rack.add(this.box("umbrella-handle", [0.08, 1.32, 0.08], this.paintedMetal, [1.28, 0.72, 0.22], [0, 0, -0.1]));
    rack.add(this.box("umbrella-tip", [0.18, 0.18, 0.18], this.mutedCloth, [1.34, 0.12, 0.22]));
    return rack;
  }

  private createScooter(): THREE.Group {
    const scooter = new THREE.Group();
    scooter.name = "ScooterSilhouette";
    scooter.userData.semantic = "parked-scooter";
    scooter.position.y = 0.34;
    for (const x of [-0.72, 0.72]) scooter.add(this.wheel("scooter-wheel", 0.32, 0.09, [x, 0, 0]));
    scooter.add(this.box("scooter-deck", [1.18, 0.14, 0.36], this.paintedMetal, [0, 0.16, 0], undefined, "solid"));
    scooter.add(this.box("scooter-body", [0.5, 0.68, 0.42], this.paintedMetal, [0.42, 0.52, 0], [0, 0, -0.18], "solid"));
    scooter.add(this.box("scooter-handle", [0.08, 1.12, 0.08], this.paintedMetal, [-0.52, 0.7, 0], [0, 0, 0.18]));
    scooter.add(this.box("scooter-basket", [0.48, 0.34, 0.4], this.paleMetal, [-0.48, 1.08, 0.18]));
    return scooter;
  }

  private createUtilityCluster(): THREE.Group {
    const utility = new THREE.Group();
    utility.name = "UtilityCluster";
    utility.userData.semantic = "domestic-utility";
    utility.add(this.box("ac-casing", [1.18, 0.74, 0.42], this.paleMetal, [-0.78, 1.72, 0], undefined, "solid"));
    utility.add(this.box("ac-vent", [0.78, 0.06, 0.04], this.paintedMetal, [-0.78, 1.5, 0.23]));
    utility.add(this.box("utility-pipe", [0.1, 1.65, 0.1], this.paleMetal, [0.12, 0.82, 0.04]));
    utility.add(this.box("meter-box", [0.5, 0.62, 0.22], this.paleMetal, [0.62, 1.18, 0.06]));
    utility.add(this.box("delivery-box", [0.62, 0.52, 0.48], this.cardboard, [1.22, 0.26, 0], undefined, "solid"));
    utility.add(this.box("delivery-box", [0.48, 0.38, 0.42], this.cardboard, [1.62, 0.19, 0.05], undefined, "solid"));
    return utility;
  }

  private createRepairSetting(): THREE.Group {
    const repair = new THREE.Group();
    repair.name = "RepairSetting";
    repair.userData.semantic = "waterside-repair";
    repair.add(this.box("repair-bench", [2.4, 0.18, 0.68], this.materials.wood, [0, 0.72, 0], undefined, "solid"));
    for (const x of [-0.95, 0.95]) repair.add(this.box("repair-bench-leg", [0.18, 0.72, 0.52], this.materials.wood, [x, 0.36, 0], undefined, "solid"));
    repair.add(this.box("tool-box", [0.78, 0.38, 0.42], this.paintedMetal, [-0.62, 1.0, 0], undefined, "solid"));
    repair.add(this.box("tool-handle", [0.46, 0.08, 0.08], this.paleMetal, [-0.62, 1.26, 0]));
    repair.add(this.box("repair-oar", [0.12, 0.08, 3.5], this.materials.wood, [0.78, 0.92, 0], [0.04, 0.28, 0]));
    repair.add(this.box("wood-offcut", [0.28, 0.2, 1.65], this.materials.wood, [1.55, 0.1, 0.12], [0, -0.14, 0], "solid"));
    return repair;
  }

  private createLaundryLine(): THREE.Group {
    const laundry = new THREE.Group();
    laundry.name = "LaundryLine";
    laundry.userData.semantic = "laundry";
    for (const x of [-2.45, 0.15]) laundry.add(this.box("laundry-post", [0.08, 2.15, 0.08], this.paintedMetal, [x, 1.08, 0], undefined, "solid"));
    laundry.add(this.box("laundry-line", [2.65, 0.025, 0.025], this.paintedMetal, [-1.15, 1.94, 0]));
    laundry.add(this.box("hanging-cloth", [0.74, 0.82, 0.025], this.mutedCloth, [-1.75, 1.48, 0]));
    laundry.add(this.box("hanging-cloth", [0.58, 0.62, 0.025], this.mutedCloth, [-0.78, 1.58, 0]));
    return laundry;
  }

  private createPlantTubs(): THREE.Group {
    const plants = new THREE.Group();
    plants.name = "PlantTubs";
    plants.userData.semantic = "waterside-plants";
    for (const [index, x] of [-0.58, 0.12, 0.72].entries()) {
      const height = 0.42 + index * 0.08;
      plants.add(this.box("plant-tub", [0.48, 0.32, 0.48], this.materials.stone, [x, 0.16, 0], undefined, "solid"));
      plants.add(this.box("plant-stem", [0.08, height, 0.08], this.foliage, [x, 0.32 + height * 0.5, 0]));
      plants.add(this.box("plant-leaves", [0.52, 0.22, 0.42], this.foliage, [x, 0.48 + height, 0], [0, index * 0.44, 0]));
    }
    return plants;
  }

  private createWupengBoat(): THREE.Group {
    const boat = this.template("wupeng-boat", "boat", [1.55, 1.72, 5.8], false);
    boat.userData.motion = {
      kind: "ambient-watercraft",
      playerControllable: false,
      bobAmplitude: 0.045,
      bobPeriodSeconds: 3.8,
      yawAmplitude: 0.018,
    };
    boat.userData.collision = {
      shape: "box",
      size: [1.55, 1.25, 5.8],
      role: "dynamic",
    } satisfies ArchitectureCollisionMetadata;
    boat.userData.collisionId = "wupeng-boat-dynamic";
    const hullGeometry = this.ownGeometry(this.createHullGeometry());
    const hull = new THREE.Mesh(hullGeometry, this.materials.wood);
    hull.name = "wupeng-hull";
    hull.position.y = 0.18;
    boat.add(hull);
    boat.add(this.box("boat-floor", [1.08, 0.1, 3.9], this.materials.wood, [0, 0.48, 0]));
    for (const z of [-1.25, 0, 1.25]) {
      boat.add(this.box("canopy-frame", [1.22, 0.08, 0.08], this.paintedMetal, [0, 1.19, z]));
      for (const x of [-0.54, 0.54]) boat.add(this.box("canopy-upright", [0.07, 0.82, 0.07], this.paintedMetal, [x, 0.84, z]));
    }
    for (const x of [-0.38, 0.38]) {
      boat.add(this.box("black-canopy-slope", [0.86, 0.08, 2.75], this.roof, [x, 1.28, 0], [0, 0, x < 0 ? -0.28 : 0.28]));
    }
    boat.add(this.box("stern-seat", [1.06, 0.12, 0.48], this.materials.wood, [0, 0.68, -2.05]));
    boat.add(this.box("sculling-oar", [0.1, 0.1, 3.25], this.materials.wood, [0.86, 0.72, -1.05], [0, -0.18, 0.22]));
    boat.add(this.anchor("BoatPathAnchor", "ambient-path-origin", [0, 0, 0], 0));
    boat.add(this.anchor("BoatMooringPoint", "mooring-point", [0, 0.4, 2.65], 0));
    return boat;
  }

  private createPavilion(): THREE.Group {
    const pavilion = this.template("pavilion", "passage", [5.8, 5.2, 5.8], false);
    pavilion.add(this.box("pavilion-plinth", [5.2, 0.38, 5.2], this.materials.pavers, [0, 0.19, 0], undefined, "walkable", "stone"));
    for (const x of [-1.8, 1.8]) {
      for (const z of [-1.8, 1.8]) {
        pavilion.add(this.box("pavilion-post", [0.24, 3.25, 0.24], this.materials.wood, [x, 1.95, z], undefined, "solid"));
      }
    }
    for (const z of [-1.9, 1.9]) pavilion.add(this.box("pavilion-beam", [4.2, 0.24, 0.26], this.materials.wood, [0, 3.45, z]));
    for (const x of [-1.9, 1.9]) pavilion.add(this.box("pavilion-beam", [0.26, 0.24, 4.2], this.materials.wood, [x, 3.45, 0]));
    const roof = this.createHipRoof(5.8, 5.8, 1.55);
    roof.position.y = 3.58;
    pavilion.add(roof);
    return pavilion;
  }

  private template(
    id: ArchitectureKind,
    category: ArchitectureTemplateMetadata["category"],
    dimensions: ArchitectureTemplateMetadata["dimensions"],
    enterable: boolean,
    waterfrontSide?: ArchitectureTemplateMetadata["waterfrontSide"],
  ): THREE.Group {
    const group = new THREE.Group();
    group.userData.template = { id, category, dimensions, enterable, waterfrontSide } satisfies ArchitectureTemplateMetadata;
    return group;
  }

  private anchor(name: string, semantic: string, position: [number, number, number], yaw: number): THREE.Object3D {
    const anchor = new THREE.Object3D();
    anchor.name = name;
    anchor.position.set(...position);
    anchor.rotation.y = yaw;
    anchor.userData.semantic = semantic;
    anchor.userData.anchor = true;
    return anchor;
  }

  private createGabledRoof(width: number, depth: number, rise: number): THREE.Group {
    const roof = new THREE.Group();
    const halfDepth = depth * 0.5;
    const slopeLength = Math.hypot(halfDepth, rise);
    const angle = Math.atan2(rise, halfDepth);
    for (const side of [-1, 1]) {
      roof.add(this.box("roof-slope", [width, 0.18, slopeLength], this.roof, [0, rise * 0.5, side * halfDepth * 0.5], [-side * angle, 0, 0]));
      const ribGeometry = this.getBoxGeometry([0.1, 0.08, slopeLength + 0.08]);
      const ribs = new THREE.InstancedMesh(ribGeometry, this.roof, Math.max(5, Math.round(width / 0.45)));
      const matrix = new THREE.Matrix4();
      const quaternion = new THREE.Quaternion().setFromEuler(new THREE.Euler(-side * angle, 0, 0));
      for (let index = 0; index < ribs.count; index += 1) {
        const x = -width * 0.5 + (index / Math.max(1, ribs.count - 1)) * width;
        matrix.compose(new THREE.Vector3(x, rise * 0.5 + 0.12, side * halfDepth * 0.5), quaternion, new THREE.Vector3(1, 1, 1));
        ribs.setMatrixAt(index, matrix);
      }
      ribs.name = "roof-tile-ribs";
      roof.add(ribs);
    }
    roof.add(this.box("ridge-cap", [width + 0.18, 0.22, 0.18], this.roof, [0, rise + 0.08, 0]));
    return roof;
  }

  private createLatticeWindow(x: number, y: number, z: number, width: number, height: number): THREE.Group {
    const window = new THREE.Group();
    window.name = "lattice-window";
    window.position.set(x, y, z);
    window.add(this.box("window-glass", [width, height, 0.08], this.glass, [0, 0, 0]));
    for (const offset of [-width * 0.5, 0, width * 0.5]) window.add(this.box("window-vertical", [0.08, height + 0.12, 0.12], this.materials.wood, [offset, 0, 0.06]));
    for (const offset of [-height * 0.5, 0, height * 0.5]) window.add(this.box("window-horizontal", [width + 0.12, 0.08, 0.12], this.materials.wood, [0, offset, 0.06]));
    return window;
  }

  private createHipRoof(width: number, depth: number, rise: number): THREE.Mesh {
    const hw = width * 0.5;
    const hd = depth * 0.5;
    const positions = new Float32Array([
      0, rise, 0, -hw, 0, -hd, hw, 0, -hd,
      0, rise, 0, hw, 0, -hd, hw, 0, hd,
      0, rise, 0, hw, 0, hd, -hw, 0, hd,
      0, rise, 0, -hw, 0, hd, -hw, 0, -hd,
    ]);
    const uvs = new Float32Array([
      0.5, 1, 0, 0, 1, 0,
      0.5, 1, 0, 0, 1, 0,
      0.5, 1, 0, 0, 1, 0,
      0.5, 1, 0, 0, 1, 0,
    ]);
    const geometry = this.ownGeometry(new THREE.BufferGeometry());
    geometry.setAttribute("position", new THREE.BufferAttribute(positions, 3));
    geometry.setAttribute("uv", new THREE.BufferAttribute(uvs, 2));
    geometry.computeVertexNormals();
    const mesh = new THREE.Mesh(geometry, this.roof);
    mesh.name = "hipped-tile-roof";
    return mesh;
  }

  private createHullGeometry(): THREE.BufferGeometry {
    const vertices = new Float32Array([
      -0.18, 0.05, -2.9, 0.18, 0.05, -2.9, -0.7, 0.42, -2.05, 0.7, 0.42, -2.05,
      -0.76, 0.42, 1.95, 0.76, 0.42, 1.95, -0.16, 0.05, 2.9, 0.16, 0.05, 2.9,
      -0.42, -0.22, -2.0, 0.42, -0.22, -2.0, -0.45, -0.22, 2.0, 0.45, -0.22, 2.0,
    ]);
    const indices = [
      0, 2, 8, 0, 8, 9, 0, 9, 1,
      1, 9, 3, 1, 3, 0,
      2, 4, 10, 2, 10, 8,
      3, 9, 11, 3, 11, 5,
      4, 6, 10, 6, 7, 11, 6, 11, 10,
      4, 10, 11, 4, 11, 5,
      2, 3, 5, 2, 5, 4,
    ];
    const geometry = new THREE.BufferGeometry();
    geometry.setAttribute("position", new THREE.BufferAttribute(vertices, 3));
    geometry.setIndex(indices);
    geometry.computeVertexNormals();
    return geometry;
  }

  private wheel(name: string, radius: number, depth: number, position: [number, number, number]): THREE.Mesh {
    const geometry = this.ownGeometry(new THREE.CylinderGeometry(radius, radius, depth, 12));
    const wheel = new THREE.Mesh(geometry, this.paintedMetal);
    wheel.name = name;
    wheel.position.set(...position);
    wheel.rotation.x = Math.PI * 0.5;
    return wheel;
  }

  private box(
    name: string,
    size: [number, number, number],
    material: THREE.Material,
    position: [number, number, number],
    rotation: [number, number, number] = [0, 0, 0],
    collision?: ArchitectureCollisionRole,
    surface?: ArchitectureCollisionMetadata["surface"],
    collisionId?: string,
  ): THREE.Mesh {
    const mesh = new THREE.Mesh(this.getBoxGeometry(size), material);
    mesh.name = name;
    mesh.position.set(...position);
    mesh.rotation.set(...rotation);
    if (collision) {
      mesh.userData.collision = { shape: "box", size: [...size], role: collision, surface } satisfies ArchitectureCollisionMetadata;
      if (collisionId) mesh.userData.collisionId = collisionId;
    }
    return mesh;
  }

  private compileSpatialMetadata(template: THREE.Group): void {
    template.updateMatrixWorld(true);
    const collisionShapes: ArchitectureCollisionShape[] = [];
    const walkableSurfaces: ArchitectureWalkableSurface[] = [];
    const ids = new Map<string, number>();
    const position = new THREE.Vector3();
    const quaternion = new THREE.Quaternion();
    const scale = new THREE.Vector3();
    const euler = new THREE.Euler(0, 0, 0, "YXZ");

    template.traverse((object) => {
      const metadata = object.userData.collision as ArchitectureCollisionMetadata | undefined;
      if (!metadata) return;
      object.matrixWorld.decompose(position, quaternion, scale);
      euler.setFromQuaternion(quaternion, "YXZ");
      const baseId = (object.userData.collisionId as string | undefined) ?? object.name;
      const count = ids.get(baseId) ?? 0;
      ids.set(baseId, count + 1);
      const id = count === 0 ? baseId : `${baseId}-${count + 1}`;
      const halfWidth = Math.abs(metadata.size[0] * scale.x) * 0.5;
      const halfDepth = Math.abs(metadata.size[2] * scale.z) * 0.5;
      const rotation = euler.y;
      if (metadata.role === "walkable") {
        walkableSurfaces.push({
          id,
          shape: "obb",
          centerX: position.x,
          centerZ: position.z,
          halfWidth,
          halfDepth,
          rotation,
          height: position.y + Math.abs(metadata.size[1] * scale.y) * 0.5,
          surface: metadata.surface ?? "stone",
        });
      } else {
        collisionShapes.push({ id, shape: "obb", centerX: position.x, centerZ: position.z, halfWidth, halfDepth, rotation });
      }
      delete object.userData.collision;
      delete object.userData.collisionId;
    });
    template.userData.collisionShapes = collisionShapes;
    template.userData.walkableSurfaces = walkableSurfaces;
  }

  private getBoxGeometry(size: readonly [number, number, number]): THREE.BoxGeometry {
    const key = size.map((value) => value.toFixed(4)).join(":");
    const existing = this.boxGeometries.get(key);
    if (existing) return existing;
    const geometry = this.ownGeometry(new THREE.BoxGeometry(...size));
    this.boxGeometries.set(key, geometry);
    return geometry;
  }

  private ownGeometry<T extends THREE.BufferGeometry>(geometry: T): T {
    this.ownedGeometries.add(geometry);
    return geometry;
  }

  private ownMaterial<T extends THREE.Material>(material: T): T {
    this.ownedMaterials.add(material);
    return material;
  }
}
